import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import AttachFile from '@material-ui/icons/AttachFile';
import ReactS3Uploader from 'react-s3-uploader';
import Send from '@material-ui/icons/Send';
import { Input } from 'components/FormInputs';
import { CircularProgress, Fab, IconButton, Toolbar, makeStyles } from '@material-ui/core';
import api from 'api';
import * as actions from 'actions/conversation';
import { isCommissionHost as isCommissionHostSelector } from 'selectors/user';
import ContactsWarning from './ContactsWarning';
import AttachmentMenu from './AttachmentMenu';
import HostFileBrowserDialog from './HostFileBrowserDialog';

const warningRegex =
  /([a-z0-9._-]+(@|\(at\)|\[at\])[a-z0-9_-]+\.[a-z0-9]+|\+?[0-9]{3}( |-)?[0-9]{2,3}( )?[0-9]{2,})/gi;
const bankRegex =
  /bank account|bankkonto|IBAN|SMS|Swish|Paypal|Whatsapp|Messenger|Swedbank|Nordea|Handelsbanken/gi;

const MESSAGE_MAX_LENGTH = 10000;

const styles = makeStyles((theme) => ({
  chatInput: {
    marginRight: theme.spacing(2),
  },
  chatForm: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  flex: {
    flex: 1,
  },
  msgWrap: {
    display: 'flex',
    alignItems: 'center',
  },
  progress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -18,
    marginLeft: -18,
  },
}));

let outer = '';

const ConversationFooter = ({
  isShowContactsWarning,
  isShowTerminationWarning,
  setInputRef,
  setFormRef,
  onFocus,
  onBlur,
  conversationId,
  disableFileInput,
  cottageId,
  isHost,
}) => {
  const classes = styles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [formValues, setFormValues] = React.useState({
    message: '',
  });
  const [uploadProgress, setUploadProgress] = React.useState(false);
  const [warningSeen, setWarningSeen] = React.useState(false);
  const [uploadingFilename, setUploadingFilename] = React.useState('');
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [browserOpen, setBrowserOpen] = React.useState(false);
  const [isAttaching, setIsAttaching] = React.useState(false);
  const [uploadKey, setUploadKey] = React.useState(0);
  const uploadSuccess = useSelector((state) => state.conversation.success);
  const uploadError = useSelector((state) => state.conversation.uploadError);
  const isCommissionHost = useSelector(isCommissionHostSelector);
  const contactsWarningRef = React.useRef();
  const fileInputRef = React.useRef();

  React.useEffect(() => {
    if (isAttaching && (uploadSuccess || uploadError)) {
      setIsAttaching(false);
    }
  }, [uploadSuccess, uploadError, isAttaching]);

  const storeFormValue = () => {
    localStorage.removeItem(`messageForm-${conversationId}`);

    if (outer) {
      localStorage.setItem(`messageForm-${conversationId}`, outer);
    }
  };

  React.useEffect(() => {
    outer = formValues.message;
  }, [formValues.message]);

  React.useEffect(() => {
    const stored = localStorage.getItem(`messageForm-${conversationId}`);

    if (stored && (!formValues || !formValues.message)) {
      setFormValues({ message: stored });
    }

    const _storeFormValue = storeFormValue;

    window.onbeforeunload = () => {
      _storeFormValue();

      return null;
    };

    return () => {
      storeFormValue();
    };
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();

    localStorage.removeItem(`messageForm-${conversationId}`);
    const text = formValues.message.trim();

    if (!text) {
      return;
    }

    setWarningSeen(false);
    dispatch(actions.postMessageRequest({ text, conversationId }));

    setFormValues({ message: '' });
  };

  const onPreprocess = (file, next) => {
    dispatch(actions.conversationUploadFileIdle());
    if (Math.floor(file.size / 1048576) > 10) {
      // convert from bytes to MB
      alert('File size should be less than 10 MB'); // eslint-disable-line
    } else {
      setUploadingFilename(file.name);
      next(file);
    }
  };

  const onUploadProgress = (progress) => {
    setUploadProgress(progress);
  };

  const resetUploader = () => {
    setUploadProgress(false);
    setUploadKey((k) => k + 1);
  };

  const onUploadSuccess = (e) => {
    if (!e.path) {
      dispatch(
        actions.conversationUploadFileFailure({
          error: { message: 'Unable to upload the file.', canTryAgain: true },
        }),
      );
      resetUploader();
      return;
    }

    dispatch(
      actions.conversationUploadFilePostInfo({
        conversationId,
        publicUrl: e.path,
        fileName: uploadingFilename,
      }),
    );

    resetUploader();
  };

  const onUploadFailure = (err) => {
    dispatch(
      actions.conversationUploadFileFailure({
        error: {
          message: (err && err.message) || 'Unable to upload the file.',
          canTryAgain: true,
        },
      }),
    );
    resetUploader();
  };

  const getSignedUrl = (file, callback) => {
    api.getSignedUrl(conversationId, file, callback);
  };

  const onChange = (e) => {
    const { value } = e.target;

    if (
      isShowContactsWarning &&
      !warningSeen &&
      (warningRegex.test(value) || bankRegex.test(value))
    ) {
      contactsWarningRef.current.handlePopoverOpen(e);
      setWarningSeen(true);
    }

    setFormValues({ message: value });
  };

  const onContactsWarningCancel = () => {
    setWarningSeen(false);
    setFormValues({ message: formValues.message.replace(warningRegex, '').replace(bankRegex, '') });
  };

  const handleAttachClick = (e) => {
    if (isHost && isCommissionHost) {
      setMenuAnchorEl(e.currentTarget);
    } else {
      fileInputRef.current.click();
    }
  };

  const handleDeviceUpload = () => {
    setMenuAnchorEl(null);
    fileInputRef.current.click();
  };

  const handleOpenBrowser = () => {
    setMenuAnchorEl(null);
    setBrowserOpen(true);
  };

  const handleAttachFromLibrary = (fileId) => {
    setBrowserOpen(false);
    dispatch(actions.conversationUploadFileIdle());
    setIsAttaching(true);
    dispatch(actions.conversationAttachFromLibraryRequest({ conversationId, fileId }));
  };

  return (
    <div ref={setFormRef}>
      <Toolbar>
        <form className={classes.chatForm} onSubmit={onSubmit}>
          <IconButton
            aria-label="Attach"
            onClick={handleAttachClick}
            disabled={uploadProgress !== false || isAttaching || disableFileInput}
          >
            <AttachFile />
            {(uploadProgress !== false || isAttaching) && (
              <CircularProgress
                size={36}
                color="inherit"
                thickness={2}
                className={classes.progress}
              />
            )}
          </IconButton>
          <ReactS3Uploader
            getSignedUrl={getSignedUrl}
            preprocess={onPreprocess}
            onProgress={onUploadProgress}
            onError={onUploadFailure}
            onFinish={onUploadSuccess}
            uploadRequestHeaders={{ 'x-amz-acl': 'private' }}
            contentDisposition="auto"
            key={uploadKey}
            style={{ display: 'none' }}
            inputRef={(ref) => {
              fileInputRef.current = ref;
            }}
          />
          {isHost && isCommissionHost && (
            <>
              <AttachmentMenu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={() => setMenuAnchorEl(null)}
                onSelectFromLibrary={handleOpenBrowser}
                onSelectFromDevice={handleDeviceUpload}
              />
              <HostFileBrowserDialog
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onSelect={handleAttachFromLibrary}
                cottageId={cottageId}
              />
            </>
          )}
          <Input
            name="message"
            multiline
            maxRows={8}
            placeholder={intl.formatMessage({
              id: 'Send message',
              defaultMessage: 'Send message',
            })}
            onChange={onChange}
            className={classNames(classes.flex, classes.chatInput)}
            onFocus={onFocus}
            onBlur={onBlur}
            maxLength={MESSAGE_MAX_LENGTH}
            InputProps={{
              'aria-label': 'Input',
              ref: setInputRef,
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              maxLength: MESSAGE_MAX_LENGTH,
            }}
            errors={{}}
            value={formValues.message}
            help={`${formValues.message.length}/${MESSAGE_MAX_LENGTH}`}
            FormHelperTextProps={{
              style: {
                textAlign: 'right',
              },
            }}
          />
          <Fab
            aria-label="Send"
            color="primary"
            size="small"
            type="submit"
            disabled={!formValues.message}
          >
            <Send />
          </Fab>
        </form>
      </Toolbar>
      {isShowContactsWarning && (
        <ContactsWarning
          innerRef={contactsWarningRef}
          onCancel={onContactsWarningCancel}
          terminationWarning={isShowTerminationWarning}
          isHost={isHost}
        />
      )}
    </div>
  );
};

ConversationFooter.propTypes = {
  isShowContactsWarning: PropTypes.bool,
  isShowTerminationWarning: PropTypes.bool,
  conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  setFormRef: PropTypes.shape({}),
  setInputRef: PropTypes.shape({}),
  disableFileInput: PropTypes.bool,
  cottageId: PropTypes.number,
  isHost: PropTypes.bool,
};

export default ConversationFooter;
